import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { uiCursor } from "./cursor";
import { cn } from "./utils";

const hasRadixSlot = typeof Slot !== "undefined";
let hasWarnedMissingSlot = false;
const warnMissingSlot = () => {
  if (hasWarnedMissingSlot) return;
  hasWarnedMissingSlot = true;
  console.warn("[ui/button] Radix Slot unavailable. Falling back to <button>.");
};

const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  for (const key in props) {
    if (!key.startsWith("_fg")) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--ui-button-radius)] text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:border-destructive ${uiCursor.interactive}`,
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground",
        toolbar:
          "rounded-[4px] border border-transparent bg-transparent text-foreground shadow-none hover:border-border hover:bg-accent active:bg-accent/80 data-[state=open]:border-border data-[state=open]:bg-accent",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
        compact: "h-7 gap-1.5 px-2.5 text-xs has-[>svg]:px-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    } &
    NativeMetadataProps
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  if (asChild && !hasRadixSlot) {
    warnMissingSlot();
  }
  const Comp = asChild && hasRadixSlot ? Slot : "button";
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  const defaultNativeRole =
    size === "icon" ? "toolbar-item" : "button";

  return (
    <Comp
      data-slot="button"
      data-native-role={nativeProps["data-native-role"] ?? defaultNativeRole}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...nativeProps}
      {...rest}
    />
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };
